import React, { FormEvent } from "react";
import { MdFileUpload } from "react-icons/md";
import { MdAddCircleOutline } from "react-icons/md";
import FileReaderInput, { Result } from "react-file-reader-input";
import { WithTranslation, withTranslation } from "react-i18next";
import maplibregl from 'maplibre-gl';
import { useEffect, useRef } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import ModalLoading from "./ModalLoading";
import Modal from "./Modal";
import InputButton from "./InputButton";

import style from "../libs/style";
import publicStyles from "../config/styles.json";

type PublicStyleProps = {
  url: string;
  thumbnailUrl: string;
  title: string;
  isCustomStyle?: boolean;
  onSelect(...args: unknown[]): unknown;
};

class PublicStyle extends React.Component<PublicStyleProps> {
  static defaultProps: Partial<PublicStyleProps> = {
    isCustomStyle: false,
  };

  render() {
    return (
      <div className="studio-public-style">
        <InputButton
          className="studio-public-style-button"
          aria-label={this.props.title}
          onClick={() => this.props.onSelect(this.props.url)}
        >
          <div className="studio-public-style-header">
            <div>{this.props.title}</div>
            <span className="studio-space" />
            <MdAddCircleOutline />
          </div>
          {this.props.isCustomStyle ? (
            <MapPreview styleUrl={this.props.url} />
          ) : (
            <div
              className="studio-public-style-thumbnail"
              style={{
                backgroundImage: `url(${this.props.thumbnailUrl})`,
              }}>
            </div>
          )}
        </InputButton>
      </div>
    );
  }
}

type ModalOpenInternalProps = {
  isOpen: boolean;
  onOpenToggle(...args: unknown[]): unknown;
  onStyleOpen(...args: unknown[]): unknown;
  customStyles: any[];
} & WithTranslation;

type ModalOpenState = {
  styleUrl: string;
  error?: string | null;
  activeRequest?: any;
  activeRequestUrl?: string | null;
};

class ModalOpenInternal extends React.Component<
  ModalOpenInternalProps,
  ModalOpenState
> {
  constructor(props: ModalOpenInternalProps) {
    super(props);
    this.state = {
      styleUrl: "",
    };
  }

  clearError() {
    this.setState({
      error: null,
    });
  }

  onCancelActiveRequest(e: Event) {
    // Else the click propagates to the underlying modal
    if (e) e.stopPropagation();

    if (this.state.activeRequest) {
      this.state.activeRequest.abort();
      this.setState({
        activeRequest: null,
        activeRequestUrl: null,
      });
    }
  }

  onStyleSelect = (styleUrl: string) => {
    this.clearError();

    let canceled: boolean = false;

    fetch(styleUrl, {
      mode: "cors",
      credentials: "same-origin",
    })
      .then(function (response) {
        return response.json();
      })
      .then((body) => {
        if (canceled) {
          return;
        }

        this.setState({
          activeRequest: null,
          activeRequestUrl: null,
        });

        const mapStyle = style.ensureStyleValidity(body);
        console.log("Loaded style ", mapStyle.id);
        this.props.onStyleOpen(mapStyle);
        this.onOpenToggle();
      })
      .catch((err) => {
        this.setState({
          error: `Failed to load: '${styleUrl}'`,
          activeRequest: null,
          activeRequestUrl: null,
        });
        console.error(err);
        console.warn("Could not open the style URL", styleUrl);
      });

    this.setState({
      activeRequest: {
        abort: function () {
          canceled = true;
        },
      },
      activeRequestUrl: styleUrl,
    });
  };

  onSubmitUrl = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    this.onStyleSelect(this.state.styleUrl);
  };

  onUpload = (_: any, files: Result[]) => {
    const [, file] = files[0];
    const reader = new FileReader();

    this.clearError();

    reader.readAsText(file, "UTF-8");
    reader.onload = (e) => {
      let mapStyle;
      try {
        mapStyle = JSON.parse(e.target?.result as string);
      } catch (err) {
        this.setState({
          error: (err as Error).toString(),
        });
        return;
      }
      mapStyle = style.ensureStyleValidity(mapStyle);
      this.props.onStyleOpen(mapStyle);
      this.onOpenToggle();
    };
    reader.onerror = (e) => console.log(e.target);
  };

  onOpenToggle() {
    this.setState({
      styleUrl: "",
    });
    this.clearError();
    this.props.onOpenToggle();
  }

  onChangeUrl = (url: string) => {
    this.setState({
      styleUrl: url,
    });
  };

  render() {
    const t = this.props.t;
    const styleOptions = publicStyles.map((style) => {
      return (
        <PublicStyle
          key={style.id}
          url={style.url}
          title={style.title}
          thumbnailUrl={style.thumbnail}
          onSelect={this.onStyleSelect}
        />
      );
    });

    let errorElement;
    if (this.state.error) {
      errorElement = (
        <div className="studio-modal-error">
          {this.state.error}
          <a
            href="#"
            onClick={() => this.clearError()}
            className="studio-modal-error-close"
          >
            ×
          </a>
        </div>
      );
    }

    return (
      <div>
        <Modal
          data-wd-key="modal:open"
          className="studio-modal--gallery"
          isOpen={this.props.isOpen}
          onOpenToggle={() => this.onOpenToggle()}
          title={t("Open Style")}
        >
          {errorElement}
          {/* Most people start from a ready-made style, so that comes first; uploading a file is the rare case. */}
          <section className="studio-modal-section studio-modal-section--shrink">
            <h1>{t("Gallery Styles")}</h1>
            <p>
              {t("Open one of the publicly available styles to start from.")}
            </p>
            <div className="studio-style-gallery-container">
              {styleOptions}
            </div>
          </section>

          <section className="studio-modal-section studio-modal-section--shrink">
            <h1>{t("Your Styles")}</h1>
            <p>{t("Choose one of your styles to begin customizing.")}</p>
            <div className="studio-style-gallery-container">
              {this.props.customStyles?.map((item, index) => {
                return (
                  <PublicStyle
                    key={index}
                    url={
                      "https://blue-band-4da3.jim9710.workers.dev/styles/?fileName=" +
                      item +
                      "&token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI5YmQzZjJkZi0yOWUwLTQxNDgtOThhMC0wOTQzNDE2ZTkxNmIiLCJzY29wZSI6WyJtYXBzIiwic2VhcmNoIl0sImlhdCI6MTczODU2NzM2OH0.WMfRgaCGg_NSNWY709gTU4mcQ_lFyHylJdR3bmONabQ"
                    }
                    isCustomStyle={true}
                    title={item.split("/")[1]}
                    thumbnailUrl={item}
                    onSelect={this.onStyleSelect}
                  />
                );
              })}
            </div>
          </section>

          <section className="studio-modal-section">
            <h1>{t("Upload Style")}</h1>
            <p>{t("Upload a JSON style from your computer.")}</p>
            <FileReaderInput
              onChange={this.onUpload}
              tabIndex={-1}
              aria-label={t("Style file")}
            >
              <InputButton className="studio-upload-button">
                <MdFileUpload /> {t("Upload")}
              </InputButton>
            </FileReaderInput>
          </section>
        </Modal>

        <ModalLoading
          isOpen={!!this.state.activeRequest}
          title={t("Loading style")}
          onCancel={(e: Event) => this.onCancelActiveRequest(e)}
          message={t("Loading: {{requestUrl}}", {
            requestUrl: this.state.activeRequestUrl,
          })}
        />
      </div>
    );
  }
}

type MapPreviewProps = {
  styleUrl: string;
};

function MapPreview({ styleUrl }: MapPreviewProps) {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;

    let map: maplibregl.Map;

    fetch(styleUrl)
      .then((res) => res.json())
      .then((style) => {
        style.layers = style.layers.map((layer: any) => {
          if (layer.type === "symbol") {
            return {
              ...layer,
              layout: {
                ...layer.layout,
                ...(layer.layout?.["text-size"] && { "text-size": 4 }),
                ...(layer.layout?.["icon-size"] && { "icon-size": 0.3 }),
              },
            };
          }
          return layer;
        });

        map = new maplibregl.Map({
          container: mapRef.current!,
          style,
          center: [13.5, 25],
          zoom: -0.9,
          interactive: false,
          dragPan: false,
          scrollZoom: false,
          boxZoom: false,
          dragRotate: false,
          keyboard: false,
          doubleClickZoom: false,
          touchZoomRotate: false,
        });
      });

    return () => map?.remove();
  }, [styleUrl]);

  return <div ref={mapRef} style={{ width: "100%", height: "106.67px" }} />;
}

const ModalOpen = withTranslation()(ModalOpenInternal);
export default ModalOpen;
